import { useState, useCallback, useContext } from 'react'
import AnalysisActivityCardBody from './AnalysisActivityCardBody'
import AnalysisCardFrame from './AnalysisCardFrame'
import DeepAnalysisEntityFrame from '../deepAnalysis/DeepAnalysisEntityFrame'
import AddToDashboardButton from './AddToDashboardButton'
import DeepAnalysisButton from '../deepAnalysis/DeepAnalysisButton'
import CardPeriodControl from '../dashboard/CardPeriodControl'
import resolveComparePeriods from '../../helpers/compareHelpers/resolveComparePeriods'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import deepAnalysisPath from '../../helpers/deepAnalysis/deepAnalysisPath'
import { client } from '../../hooks/Client'

const EMPTY_PERIOD = { period: { start: null, end: null }, compare: { enabled: false, start: null, end: null } }

/**
 * Analyse-page wrapper around the shared Activity card body. Owns the transient
 * persona filter + per-output / per-event-field color override state and
 * exposes pin-snapshot callbacks. State is React-only — page reload resets
 * everything.
 *
 * @param {object} props
 * @param {object} props.activity - The Activity doc to render.
 * @param {Array<object>} [props.personas] - Personas list (loaded once at the page level in `Analysis.jsx`).
 * @param {boolean} [props.personasEnabled] - Whether the persona chip should render at all.
 * @param {boolean} [props.showDeepAnalysis] - Show the "Diepte-analyse" action in the card header (Analyse page only — the deep-analysis page itself omits it).
 * @param {boolean} [props.frameless] - Render without the card box (deep-analysis page): no chrome, no pins, details shown inline.
 * @param {React.ComponentType<{children: React.ReactNode}>} [props.frameComponent] - Overrides the frame; the deep-analysis layout passes a passthrough because it provides the slot contexts itself.
 */
const AnalysisActivityCard = ({ activity, personas, personasEnabled, showDeepAnalysis = false, frameless = false, frameComponent }) => {
    // Local (transient) state
    const [selectedPersonaId, setSelectedPersonaId] = useState('')
    const [colorOverridesByOutput, setColorOverridesByOutput] = useState({})
    const [graphTypeByOutput, setGraphTypeByOutput] = useState({})
    const [colorOverridesByEventField, setColorOverridesByEventField] = useState({})
    const [localPeriodValue, setPeriodValue] = useState(EMPTY_PERIOD)
    // On the deep-analysis page the page owns the period (url query) and the
    // card follows it; on 'Analyseren' the card keeps its own.
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const pagePeriod = useContext(DeepAnalysisPeriodContext)
    const periodValue = pagePeriod ? (pagePeriod.periodValue || EMPTY_PERIOD) : localPeriodValue
    const { rangeA, rangeB, compareActive } = resolveComparePeriods(periodValue)

    const handleSaveOutputOptions = useCallback((patch, outputId) => {
        if (!outputId) return
        if (Object.prototype.hasOwnProperty.call(patch, 'ColorOverrides')) {
            setColorOverridesByOutput(prev => ({ ...prev, [outputId]: patch.ColorOverrides }))
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'GraphType')) {
            setGraphTypeByOutput(prev => ({ ...prev, [outputId]: patch.GraphType }))
        }
    }, [])

    const handleSaveEventFieldOptions = useCallback((patch, fieldId) => {
        if (!fieldId) return
        if (Object.prototype.hasOwnProperty.call(patch, 'ColorOverrides')) {
            setColorOverridesByEventField(prev => ({ ...prev, [fieldId]: patch.ColorOverrides }))
        }
    }, [])

    const getCardSnapshot = useCallback(() => ({
        selectedPersonaId,
        colorOverridesByOutput,
        graphTypeByOutput,
        colorOverridesByEventField,
        period: periodValue.period,
        compare: periodValue.compare,
    }), [selectedPersonaId, colorOverridesByOutput, graphTypeByOutput, colorOverridesByEventField, periodValue])

    const getItemSnapshot = useCallback(() => ({
        selectedPersonaId,
        colorOverridesByOutput,
        graphTypeByOutput,
        colorOverridesByEventField,
        period: periodValue.period,
        compare: periodValue.compare,
    }), [selectedPersonaId, colorOverridesByOutput, graphTypeByOutput, colorOverridesByEventField, periodValue])

    // The pin button stays in the header; the deep-analysis icon portals down
    // into the card's bottom bar (see DeepAnalysisButton — no `inline`).
    const headerAction = showDeepAnalysis ? (
        <span className='analysis-card-header-actions'>
            <CardPeriodControl value={periodValue} onChange={setPeriodValue} />
            <DeepAnalysisButton type='activity' periodValue={periodValue} id={activity.ID} />
            <AddToDashboardButton
                type='activity'
                activityId={activity.ID}
                label={activity.Activity}
                showLabel
                getSnapshot={getCardSnapshot}
            />
        </span>
    ) : undefined

    // The title is a second way into the deep analysis: the icon only has a
    // name on hover (testronde 2026-10-28, B5). Same path builder as the icon.
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const titleLink = showDeepAnalysis ? deepAnalysisPath(client, 'activity', activity.ID, periodValue) : undefined

    const Frame = frameComponent || (frameless ? DeepAnalysisEntityFrame : AnalysisCardFrame)

    return (
        <Frame>
            <AnalysisActivityCardBody
                titleLink={titleLink}
                activity={activity}
                headerAction={headerAction}
                hidePins={frameless}
                personas={personas}
                personasEnabled={personasEnabled}
                selectedPersonaId={selectedPersonaId}
                onPersonaChange={setSelectedPersonaId}
                showOptions
                colorOverridesByOutput={colorOverridesByOutput}
                graphTypeByOutput={graphTypeByOutput}
                onSaveOutputOptions={handleSaveOutputOptions}
                colorOverridesByEventField={colorOverridesByEventField}
                onSaveEventFieldOptions={handleSaveEventFieldOptions}
                getCardSnapshot={getCardSnapshot}
                getItemSnapshot={getItemSnapshot}
                compareActive={compareActive}
                rangeA={rangeA}
                rangeB={rangeB}
            />
        </Frame>
    )
}

export default AnalysisActivityCard
